import React, { useState, useEffect } from 'react';
import { 
  CheckCircleIcon, 
  ExclamationTriangleIcon,
  UserIcon,
  CalendarIcon,
  ArrowTopRightOnSquareIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useAuth } from '@/state/auth/AuthProvider';
import { Link } from 'react-router-dom';
import api from '@/services/api';

interface KeyAction {
  id: string;
  title: string;
  description?: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'RETURNED';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  due_date?: string;
  assigned_to_user_name?: string;
  created_by_user_name?: string;
}

interface KeyActionsWidgetProps {
  tenantId: string;
  limit?: number;
}

export const KeyActionsWidget: React.FC<KeyActionsWidgetProps> = ({ 
  tenantId, 
  limit = 5 
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [actions, setActions] = useState<KeyAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (tenantId && user) {
      loadKeyActions();
    }
  }, [tenantId, user]);

  const loadKeyActions = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch actions assigned to me and actions I created, then combine them
      const [assignedResponse, createdResponse] = await Promise.all([
        api.get(`/actions/my-actions`),
        api.get(`/actions/i-created`)
      ]);
      
      // Validate response structures
      const assignedData = assignedResponse.data;
      const createdData = createdResponse.data;
      
      if (!assignedData || typeof assignedData !== 'object') {
        throw new Error('Invalid response format from my-actions: expected object');
      }
      if (!createdData || typeof createdData !== 'object') {
        throw new Error('Invalid response format from i-created: expected object');
      }

      // Combine actions from both endpoints and remove duplicates (by ID)
      const assignedActions = assignedData.data || [];
      const createdActions = createdData.data || [];
      const allActionsMap = new Map<string, KeyAction>();
      
      // Add assigned actions
      assignedActions.forEach((action: KeyAction) => {
        allActionsMap.set(action.id, action);
      });
      
      // Add created actions (will overwrite if duplicate, but that's fine)
      createdActions.forEach((action: KeyAction) => {
        allActionsMap.set(action.id, action);
      });
      
      // Convert map to array
      const allActions = Array.from(allActionsMap.values());
      
      // Debug logging to see what we're getting
      console.log('KeyActionsWidget - All actions fetched:', {
        assignedCount: assignedActions.length,
        createdCount: createdActions.length,
        totalUnique: allActions.length,
        actions: allActions.map(a => ({ id: a.id, title: a.title, priority: a.priority, status: a.status }))
      });
      
      // Filter for active status (PENDING, IN_PROGRESS) - show all priorities
      // This ensures users see all their active actions, not just high priority ones
      const activeActions = allActions.filter((action: KeyAction) => {
        const isActive = action.status === 'PENDING' || action.status === 'IN_PROGRESS';
        if (!isActive) {
          console.log('KeyActionsWidget - Filtered out action:', {
            id: action.id,
            title: action.title,
            status: action.status,
            reason: 'Not in PENDING or IN_PROGRESS status'
          });
        }
        return isActive;
      });
      
      console.log('KeyActionsWidget - Active actions after filtering:', {
        total: activeActions.length,
        actions: activeActions.map(a => ({ id: a.id, title: a.title, priority: a.priority, status: a.status }))
      });
      
      // Sort by priority (CRITICAL first, then HIGH, then others) and due date (overdue first)
      const actionsList = activeActions.sort((a: KeyAction, b: KeyAction) => {
        const priorityOrder = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
        const priorityDiff = (priorityOrder[a.priority] || 3) - (priorityOrder[b.priority] || 3);
        if (priorityDiff !== 0) return priorityDiff;
        
        // If same priority, sort by due date (overdue first)
        if (a.due_date && b.due_date) {
          return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
        }
        if (a.due_date) return -1;
        if (b.due_date) return 1;
        return 0;
      }).slice(0, limit);

      setActions(actionsList);
    } catch (error: any) {
      console.error('Error loading key actions:', error);
      // Extract error message from axios error response
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to load key actions';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'CRITICAL':
        return 'text-red-600';
      case 'HIGH':
        return 'text-orange-600';
      case 'MEDIUM':
        return 'text-yellow-600';
      case 'LOW':
        return 'text-green-600';
      default:
        return 'text-[var(--color-secondary-text)]';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'CRITICAL':
      case 'HIGH':
        return ExclamationTriangleIcon;
      default:
        return CheckCircleIcon;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'text-green-600';
      case 'IN_PROGRESS':
        return 'text-blue-600';
      case 'PENDING':
        return 'text-yellow-600';
      case 'CANCELLED':
        return 'text-gray-600';
      case 'RETURNED':
        return 'text-orange-600';
      default:
        return 'text-[var(--color-secondary-text)]';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(date);
    dueDate.setHours(0, 0, 0, 0);
    
    if (dueDate < today) {
      return `Overdue: ${date.toLocaleDateString()}`;
    }
    return date.toLocaleDateString();
  };

  const isOverdue = (dateString?: string) => {
    if (!dateString) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(dateString);
    dueDate.setHours(0, 0, 0, 0);
    return dueDate < today;
  };

  if (loading) {
    return (
      <Card className="bg-[var(--color-surface-bg)] border border-[var(--color-border)] shadow-none">
        <CardHeader>
          <CardTitle className="text-[var(--color-surface-text)] flex items-center">
            <CheckCircleIcon className="h-5 w-5 mr-2" />
            {t('dashboard.keyActions') || 'Key Actions'}
          </CardTitle>
          <CardDescription className="text-[var(--color-secondary-text)] opacity-75">
            {t('dashboard.keyActionsDescription') || 'High priority actions requiring attention'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[var(--color-accent-text)] mx-auto"></div>
            <p className="text-sm text-[var(--color-secondary-text)] opacity-75 mt-2">{t('common.loading')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="bg-[var(--color-surface-bg)] border border-[var(--color-border)] shadow-none">
        <CardHeader>
          <CardTitle className="text-[var(--color-surface-text)] flex items-center">
            <CheckCircleIcon className="h-5 w-5 mr-2" />
            {t('dashboard.keyActions') || 'Key Actions'}
          </CardTitle>
          <CardDescription className="text-[var(--color-secondary-text)] opacity-75">
            {t('dashboard.keyActionsDescription') || 'High priority actions requiring attention'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <p className="text-sm text-red-700 font-medium">{error}</p>
            <button 
              onClick={loadKeyActions}
              className="mt-2 text-sm text-[var(--color-accent-text)] hover:underline"
            >
              {t('common.retry')}
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-[var(--color-surface-bg)] border border-[var(--color-border)] shadow-none">
      <CardHeader>
        <CardTitle className="text-[var(--color-surface-text)] flex items-center">
          <CheckCircleIcon className="h-5 w-5 mr-2" />
          {t('dashboard.keyActions') || 'Key Actions'}
        </CardTitle>
        <CardDescription className="text-[var(--color-secondary-text)] opacity-75">
          {t('dashboard.keyActionsDescription') || 'High priority actions requiring attention'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {actions.length === 0 ? (
          <div className="text-center py-4">
            <CheckCircleIcon className="h-12 w-12 opacity-50 mx-auto mb-2" />
            <p className="text-sm opacity-75">
              {t('dashboard.noKeyActions') || 'No high priority actions'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {actions.map((action) => {
              const PriorityIcon = getPriorityIcon(action.priority);
              const overdue = isOverdue(action.due_date);
              return (
                <Link
                  key={action.id}
                  to={`/actions/${action.id}`}
                  className="group flex items-center justify-between p-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)] hover:bg-[var(--color-accent-bg)] hover:bg-opacity-10 hover:text-[var(--color-accent-text)] transition-colors cursor-pointer"
                >
                  <div className="flex-1 min-w-0 mr-2">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="text-sm font-medium truncate group-hover:text-[var(--color-accent-text)] transition-colors">
                        {action.title}
                      </h4>
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-[var(--color-surface-bg)] ${getPriorityColor(action.priority)}`}>
                        <PriorityIcon className="h-2.5 w-2.5 mr-0.5" />
                        {action.priority}
                      </span>
                    </div>
                    <div className="flex items-center space-x-3 text-xs opacity-75">
                      <span className={`font-medium ${getStatusColor(action.status)}`}>
                        {action.status}
                      </span>
                      {action.due_date && (
                        <span className={`flex items-center ${overdue ? 'text-red-700 font-semibold' : ''}`}>
                          <CalendarIcon className="h-2.5 w-2.5 mr-0.5" />
                          {formatDate(action.due_date)}
                        </span>
                      )}
                      {action.assigned_to_user_name && (
                        <span className="flex items-center">
                          <UserIcon className="h-2.5 w-2.5 mr-0.5" />
                          {action.assigned_to_user_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <EyeIcon className="h-3.5 w-3.5 flex-shrink-0 opacity-50 group-hover:opacity-100" />
                </Link>
              );
            })}
          </div>
        )}

        {/* View All Actions Link */}
        <div className="mt-4 pt-4 border-t border-[var(--color-border)]">
          <Link
            to="/actions"
            className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] hover:bg-opacity-90 rounded-md transition-colors"
          >
            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 mr-1.5" />
            {t('dashboard.viewAllActions') || 'View All Actions'}
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};

